import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Input, Select, Table } from "antd";
import { FileExcelOutlined, SearchOutlined } from "@ant-design/icons";
import utils from "utils";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { find } from "lodash";
import { FIRESTORE_USER_ANALYTICS_TABLE } from "../../../../constants/FirestoreConstant";
import { useFirestoreConnect } from "react-redux-firebase";
import { isLandelijk } from "../../../../functions/region";
import ExcelExport from "../../../../components/shared-components/ExcelExport";
import { getCustomerExcelExportData } from "../../../../functions/excel";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../../constants/DateConstant";
import { COMPANY_CONSTANTS } from "../../../../constants/CompanyConstants";
import dayjs from "dayjs";

const Customers = () => {
  const webshopUsers = useSelector((state) => state.firestoreReducer.ordered.webshopUsers);
  const loyaltyUsers = useSelector((state) => state.firestoreReducer.ordered.loyaltyUsers);
  const orders = useSelector((state) => state.firestoreReducer.ordered.orders);

  const [filteredCustomerList, setFilteredCustomerList] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [regionFilter, setRegionFilter] = useState("all");
  const { t } = useTranslation();

  // Connect to Firestore to get all RFM analyses
  useFirestoreConnect([{ collection: FIRESTORE_USER_ANALYTICS_TABLE }]);

  const userAnalytics = useSelector((state) => state.firestoreReducer.ordered.userAnalytics);

  const filterByRegion = (list, region) => {
    if (!list || region === "all") return list;
    return list.filter((customer) => {
      const zip = customer?.address?.delivery?.zipCode;
      const landelijk = isLandelijk(zip);
      return region === "landelijk" ? landelijk : !landelijk;
    });
  };

  const customerList = useMemo(() => {
    if (!webshopUsers || !loyaltyUsers) return null;
    return webshopUsers.map((user) => {
      const loyaltyUser = find(loyaltyUsers, (u) => u.id === user.id);
      const userAnalyticsObject = userAnalytics ? find(userAnalytics, (ua) => ua.id === user.id) : null;
      const mergedUser = { ...user };
      if (loyaltyUser) {
        mergedUser.currentPoints = loyaltyUser.currentPoints;
        mergedUser.totalPointsEarned = loyaltyUser.totalPointsEarned;
        mergedUser.totalPointsSpent = loyaltyUser.totalPointsSpent;
      }
      if (userAnalyticsObject) {
        mergedUser.currentRfm = userAnalyticsObject.currentRfm;
        mergedUser.rfmHistory = userAnalyticsObject.rfmHistory;
        mergedUser.rfmMetrics = userAnalyticsObject.rfmMetrics;
      }
      return mergedUser;
    });
  }, [webshopUsers, loyaltyUsers, userAnalytics]);

  useEffect(() => {
    if (customerList) {
      setFilteredCustomerList(customerList);
    }
  }, [customerList]);

  const tableColumns = [
    {
      title: t("email"),
      dataIndex: "email",
      render: (data) => <span>{data}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "email"),
    },
    {
      title: t("name"),
      dataIndex: "firstName",
      render: (_, data) =>
        data?.firstName && data?.lastName ? <span>{data.firstName + " " + data.lastName}</span> : <span> - </span>,
    },
    {
      title: t("customer_segment"),
      dataIndex: ["currentRfm", "segment"],
    },
    {
      title: t("customer.loyalty_points_current"),
      dataIndex: "currentPoints",
      render: (data) => (data ? <span>{data}</span> : <span> - </span>),
    },
    {
      title: t("customer.loyalty_points_total"),
      dataIndex: "totalPointsEarned",
      render: (data) => (data ? <span>{data}</span> : <span> - </span>),
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalPointsEarned"),
    },
    {
      title: t("customer.loyalty_points_spent"),
      dataIndex: "totalPointsSpent",
      render: (data) => (data ? <span>{data}</span> : <span> - </span>),
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalPointsSpent"),
    },
  ];

  const onSearch = (e) => {
    const value = e.currentTarget.value;
    setSearchText(value);
    const regionFiltered = filterByRegion(customerList, regionFilter);
    if (value) {
      setFilteredCustomerList(utils.wildCardSearchOnCustomer(regionFiltered, value));
    } else {
      setFilteredCustomerList(regionFiltered);
    }
  };

  const onRegionFilterChange = (value) => {
    setRegionFilter(value);
    setSearchText("");
    setFilteredCustomerList(filterByRegion(customerList, value));
  };

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Input
            value={searchText}
            placeholder={t("search")}
            prefix={<SearchOutlined />}
            onChange={(e) => onSearch(e)}
            style={{ width: "fit-content" }}
          />{" "}
          <Select
            defaultValue="all"
            style={{ minWidth: 180 }}
            onChange={onRegionFilterChange}
            options={[
              { label: "Alle", value: "all" },
              { label: "Regionaal", value: "regionaal" },
              { label: "Landelijk", value: "landelijk" },
            ]}
          />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!!filteredCustomerList && filteredCustomerList.length > 0 && (
            <Button>
              <ExcelExport
                data={getCustomerExcelExportData(filteredCustomerList, orders)}
                fileName={
                  COMPANY_CONSTANTS.WEBSITE_NAME +
                  " klanten export " +
                  dayjs().format(DATE_FORMAT_DD_MM_YYYY) +
                  (regionFilter !== "all" ? " " + regionFilter.toUpperCase() : "")
                }
              >
                <FileExcelOutlined /> {t("export")}
              </ExcelExport>
            </Button>
          )}
        </div>
      </div>
      <div className="table-responsive">
        <Table
          showSorterTooltip={false}
          columns={tableColumns}
          dataSource={filteredCustomerList}
          rowKey="id"
          scroll={{ x: 1300 }}
        />
      </div>
    </Card>
  );
};

export default Customers;
